import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import NotificationCenter from '../components/NotificationCenter';
import InlineFeedback from '../components/InlineFeedback';

export const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) {
    return envBase.trim().replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const currentHost = window.location.hostname;
    if (currentHost === 'localhost' || currentHost === '127.0.0.1' || currentHost === '::1' || currentHost === '[::1]') {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${currentHost}:8000/api`;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`;

const WorkerDashboard = ({ onLogout, setNotification }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [metrics, setMetrics] = useState({
    pipeline: {},
    upcoming_paid_bookings: 0,
    outstanding_payment_required: 0,
    captured_revenue_cents: 0,
    deposit_revenue_cents: 0,
    final_payment_revenue_cents: 0,
    tip_revenue_cents: 0,
    net_revenue_cents: 0,
  });
  const [metricsError, setMetricsError] = useState('');
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);

  const formatCurrencyFromCents = (cents) =>
    `$${((Number(cents || 0) || 0) / 100).toFixed(2)}`;

  useEffect(() => {
    if (!token) return;

    const loadMetrics = async () => {
      try {
        setIsLoadingMetrics(true);
        const response = await axios.get(apiUrl('bookings/worker_revenue_metrics/'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMetrics(response.data || {});
        setMetricsError('');
      } catch (error) {
        console.error('Failed to load worker revenue metrics:', error);
        setMetricsError('Unable to load worker revenue metrics right now.');
      } finally {
        setIsLoadingMetrics(false);
      }
    };

    loadMetrics();
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userRole');
    onLogout?.();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#FAF8F6] px-3 py-4 text-[#2E2E2E] sm:px-4 sm:py-8">
      <div className="max-w-5xl mx-auto space-y-8">
        <header className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] items-start sm:items-center gap-3">
          <h1 className="text-2xl font-bold text-[#6D3B7E] sm:text-3xl">Worker Dashboard</h1>
          <NotificationCenter embedded setNotification={setNotification} />
          <button
            onClick={handleLogout}
            className="bg-[#EF5350] hover:bg-red-600 text-white px-4 py-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E]"
          >
            Logout
          </button>
        </header>

        <p className="text-[#555]">Welcome back! Manage your schedule and services from here.</p>

        <section className="bg-white rounded-xl shadow p-5 space-y-4" aria-busy={isLoadingMetrics}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-[#6D3B7E]">Revenue Snapshot</h2>
          </div>

          <InlineFeedback type={metricsError ? 'error' : 'info'} message={metricsError || (isLoadingMetrics ? 'Loading revenue metrics...' : '')} />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            <div className="rounded-lg border p-3 bg-[#F9F7FC]">
              <p className="text-gray-600">Captured Service Revenue</p>
              <p className="text-lg font-bold text-[#6D3B7E]">
                {formatCurrencyFromCents(metrics.captured_revenue_cents)}
              </p>
            </div>
            <div className="rounded-lg border p-3 bg-[#F9F7FC]">
              <p className="text-gray-600">Final Balances</p>
              <p className="text-lg font-bold text-[#6D3B7E]">
                {formatCurrencyFromCents(metrics.final_payment_revenue_cents)}
              </p>
            </div>
            <div className="rounded-lg border p-3 bg-[#F9F7FC]">
              <p className="text-gray-600">Tips Received</p>
              <p className="text-lg font-bold text-[#6D3B7E]">
                {formatCurrencyFromCents(metrics.tip_revenue_cents)}
              </p>
            </div>
            <div className="rounded-lg border p-3 bg-[#F9F7FC]">
              <p className="text-gray-600">Net Payments</p>
              <p className="text-lg font-bold text-[#6D3B7E]">
                {formatCurrencyFromCents(metrics.net_revenue_cents)}
              </p>
            </div>
            <div className="rounded-lg border p-3 bg-[#F9F7FC]">
              <p className="text-gray-600">Upcoming Paid Bookings</p>
              <p className="text-lg font-bold text-[#6D3B7E]">{metrics.upcoming_paid_bookings || 0}</p>
            </div>
            <div className="rounded-lg border p-3 bg-[#F9F7FC]">
              <p className="text-gray-600">Payment Outstanding</p>
              <p className="text-lg font-bold text-[#6D3B7E]">{metrics.outstanding_payment_required || 0}</p>
            </div>
            <div className="rounded-lg border p-3 bg-[#F9F7FC]">
              <p className="text-gray-600">Confirmed Pipeline</p>
              <p className="text-lg font-bold text-[#6D3B7E]">
                {metrics.pipeline?.confirmed || 0}
              </p>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/mybookings')}
            className="rounded-xl bg-white p-4 text-left shadow transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E] sm:p-6"
          >
            <h2 className="text-xl font-semibold text-[#6D3B7E] mb-2">📋 My Bookings</h2>
            <p className="text-sm text-gray-600">Open booking details to cancel/reject/reschedule as needed.</p>
          </button>

          <button
            onClick={() => navigate('/calendar')}
            className="rounded-xl bg-white p-4 text-left shadow transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E] sm:p-6"
          >
            <h2 className="mb-2 text-xl font-semibold text-[#6D3B7E]">🗓️ Calendar</h2>
            <p className="text-sm text-gray-600">See assigned client appointments in a daily and monthly calendar.</p>
          </button>

          <button
            onClick={() => navigate('/worker-availability')}
            className="rounded-xl bg-white p-4 text-left shadow transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E] sm:p-6"
          >
            <h2 className="text-xl font-semibold text-[#6D3B7E] mb-2">📅 Availability</h2>
            <p className="text-sm text-gray-600">Set and update available dates and time slots.</p>
          </button>

          <button
            onClick={() => navigate('/my-services')}
            className="rounded-xl bg-white p-4 text-left shadow transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E] sm:p-6"
          >
            <h2 className="text-xl font-semibold text-[#6D3B7E] mb-2">💇 My Services</h2>
            <p className="text-sm text-gray-600">Create and customize services you offer.</p>
          </button>

          <button
            onClick={() => navigate('/')}
            className="rounded-xl bg-white p-4 text-left shadow transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D3B7E] sm:p-6"
          >
            <h2 className="text-xl font-semibold text-[#6D3B7E] mb-2">🏠 Home</h2>
            <p className="text-sm text-gray-600">Go back to the landing page.</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkerDashboard;
